import React, { useEffect, useRef } from 'react';
import { UploadElement } from 'is24-corecss';
import { IconS24CrossCircle24Filled, IconS24DocumentPlus24 } from '@is24/cosma-ui-icons';
import { useTranslation } from 'react-i18next';
import AttachedFiles from '../attachedFiles/AttachedFiles';

export interface AttachedFile {
  id: string;
  file: File;
  validationErrorMessage?: string;
}

interface FileUploadProps {
  title: string;
  subtitle?: string;
  accept?: string;
  maxFileSizeBytes?: number;
  maxFileSizeErrorMessage?: string;
  files: AttachedFile[];
  onFilesChange: (files: AttachedFile[]) => void;
  errorMessage?: string;
  hasError?: boolean;
  disabled?: boolean;
  /** When true, new selections are appended and the file dialog allows multi-select. */
  multiple?: boolean;
}

const createAttachedFileId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const toAttachedFiles = (
  files: File[],
  maxFileSizeBytes?: number,
  maxFileSizeErrorMessage?: string,
): AttachedFile[] => {
  return files.map((file) => ({
    id: createAttachedFileId(),
    file,
    validationErrorMessage: maxFileSizeBytes && file.size > maxFileSizeBytes ? maxFileSizeErrorMessage : undefined,
  }));
};

const mergeSelectedFiles = (
  currentFiles: AttachedFile[],
  selectedFiles: AttachedFile[],
  multiple: boolean,
): AttachedFile[] => {
  /* istanbul ignore next -- native file inputs do not dispatch a change event for an empty selection in jsdom/browser usage. */
  if (selectedFiles.length === 0) {
    return currentFiles;
  }

  return multiple ? [...currentFiles, ...selectedFiles] : selectedFiles;
};

const clearNativeFileInput = (rootElement: HTMLDivElement | null) => {
  const input = rootElement?.querySelector<HTMLInputElement>('input[type="file"]');

  /* istanbul ignore else -- UploadElement.Root always renders its hidden native file input. */
  if (input) {
    input.value = '';
  }
};

export const FileUpload: React.FC<FileUploadProps> = ({
  title,
  subtitle,
  accept,
  maxFileSizeBytes,
  maxFileSizeErrorMessage,
  files,
  onFilesChange,
  errorMessage,
  hasError = false,
  disabled = false,
  multiple = true,
}) => {
  const { t } = useTranslation();
  const uploadRootRef = useRef<HTMLDivElement>(null);

  // UploadElement does not expose `multiple` on its hidden input.
  useEffect(() => {
    const input = uploadRootRef.current?.querySelector<HTMLInputElement>('input[type="file"]');

    /* istanbul ignore else -- UploadElement.Root always renders its hidden native file input. */
    if (input) {
      input.multiple = multiple;
      input.disabled = disabled;
    }
  }, [disabled, multiple]);

  const handleFileSelect = (fileList: FileList) => {
    if (disabled) return;

    onFilesChange(
      mergeSelectedFiles(
        files,
        toAttachedFiles(Array.from(fileList), maxFileSizeBytes, maxFileSizeErrorMessage ?? t('FileUpload.error')),
        multiple,
      ),
    );
    clearNativeFileInput(uploadRootRef.current);
  };

  const handleRemove = (id: string) => {
    if (disabled) return;

    onFilesChange(files.filter((attached) => attached.id !== id));
    clearNativeFileInput(uploadRootRef.current);
  };

  const fileValidationErrorMessage = files.find(
    ({ validationErrorMessage }) => validationErrorMessage,
  )?.validationErrorMessage;
  const uploadErrorMessage = fileValidationErrorMessage || errorMessage || t('FileUpload.error');
  const hasUploadError = hasError || !!fileValidationErrorMessage;

  return (
    <div>
      <UploadElement.Root
        ref={uploadRootRef}
        status={hasUploadError ? 'error' : 'empty'}
        accept={accept}
        disabled={disabled}
        onFileSelect={handleFileSelect}
      >
        <UploadElement.Dropzone>
          <UploadElement.Icon>
            <IconS24DocumentPlus24 />
          </UploadElement.Icon>
          <UploadElement.Content>
            <UploadElement.Title>{title}</UploadElement.Title>
            <UploadElement.Description>{subtitle ?? t('FileUpload.description')}</UploadElement.Description>
          </UploadElement.Content>
        </UploadElement.Dropzone>

        <UploadElement.Error>
          <IconS24CrossCircle24Filled />
          {uploadErrorMessage}
        </UploadElement.Error>
      </UploadElement.Root>

      {files.length > 0 && (
        <AttachedFiles
          files={files}
          ariaLabel={t('FileUpload.attachedFiles')}
          removeLabel={t('FileUpload.remove')}
          onRemove={handleRemove}
        />
      )}
    </div>
  );
};

export default FileUpload;
